import {
  Box,
  Button,
  Collapse,
  DrawerFooter,
  DrawerHeader,
  Flex,
  HStack,
  Icon,
  Stack,
  Text,
  Tooltip,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import {
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Copy,
  ExternalLink,
  X,
  XCircle,
  RefreshCw,
} from 'lucide-react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { useCallback, useMemo, useRef, useEffect } from 'react';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import type { NatRuleColumn, NatFileType } from '@duke-hq/avetmiss/NATTypes';
import type { ValidationError } from '@duke-hq/avetmiss/validation/fieldValidation';
import { StudentUserDetail } from '../Students/StudentUserDetail';
import { useNATCache } from './contexts/useNATCache';
import { transformStudentApiResponseToAvetmissCache } from '@duke-hq/avetmiss/formatters/NAT00080';
import { iconSize } from '@duke-hq/ui/constants/scalable';
import { rowHasLinkableIdentifier, trimmedIdentifier } from './rowLinkTargets';

function openInNewTab(path: string) {
  window.open(path, '_blank', 'noopener,noreferrer');
}

function RowActionBar({
  rowData,
  knownStudentId,
}: {
  rowData: Record<string, string | undefined>;
  /**
   * NAT00080 rows only ever resolve a student (no course/unit/enrolment fields exist on that
   * row shape), and NATRowDetails already runs a student lookup via avetmiss.sync for the
   * embedded edit form whenever the row has errors/warnings. Passing that result in here skips
   * firing a second, identical getStudentByStudentDataAttribute lookup for the same row.
   */
  knownStudentId?: string;
}): React.ReactElement | null {
  const programIdentifier = rowData['programIdentifier'];
  const needsResolve = knownStudentId == null && rowHasLinkableIdentifier(rowData);

  const { data: targets } = trpcAdmin.avetmiss.resolveRowTargets.useQuery(
    {
      clientIdentifier: trimmedIdentifier(rowData['clientIdentifier']),
      programIdentifier: trimmedIdentifier(programIdentifier),
      subjectIdentifier: trimmedIdentifier(rowData['subjectIdentifier']),
    },
    { enabled: needsResolve },
  );

  if (knownStudentId == null && targets == null) {
    return null;
  }

  const studentId = knownStudentId ?? targets?.studentId;

  const hasAnyTarget =
    studentId != null ||
    targets?.courseId != null ||
    targets?.unitId != null ||
    targets?.enrolmentMatchCount != null;

  if (!hasAnyTarget) {
    return null;
  }

  return (
    <Flex gap={2} flexWrap="wrap">
      {studentId != null && (
        <Button
          size="sm"
          variant="outline"
          colorScheme="gray"
          rightIcon={<Icon as={ExternalLink} boxSize={iconSize.xs} />}
          onClick={() => openInNewTab(`/student/${studentId}`)}
        >
          <Trans>Open student</Trans>
        </Button>
      )}
      {targets?.courseId != null && (
        <Button
          size="sm"
          variant="outline"
          colorScheme="gray"
          rightIcon={<Icon as={ExternalLink} boxSize={iconSize.xs} />}
          onClick={() => targets.courseId != null && openInNewTab(`/courses/${targets.courseId}`)}
        >
          <Trans>Open course</Trans>
        </Button>
      )}
      {targets?.unitId != null && (
        <Button
          size="sm"
          variant="outline"
          colorScheme="gray"
          rightIcon={<Icon as={ExternalLink} boxSize={iconSize.xs} />}
          onClick={() => targets.unitId != null && openInNewTab(`/units/${targets.unitId}`)}
        >
          <Trans>Open unit</Trans>
        </Button>
      )}
      {targets?.enrolmentMatchCount != null && studentId != null && (
        <Tooltip
          label={
            targets.enrolmentId != null
              ? undefined
              : targets.enrolmentMatchCount === 0
                ? t`No exact enrolment match found for ${programIdentifier ?? ''} — showing all enrolments instead`
                : t`${targets.enrolmentMatchCount} enrolments match ${programIdentifier ?? ''} — can't resolve to one, showing all enrolments`
          }
          hasArrow
          isDisabled={targets.enrolmentId != null}
        >
          <Button
            size="sm"
            variant="outline"
            colorScheme="gray"
            rightIcon={<Icon as={ExternalLink} boxSize={iconSize.xs} />}
            onClick={() => {
              openInNewTab(
                targets.enrolmentId != null
                  ? `/enrolment/${targets.enrolmentId}`
                  : `/student/${studentId}/enrolments`,
              );
            }}
          >
            {targets.enrolmentId != null ? (
              <Trans>Open enrolment</Trans>
            ) : targets.enrolmentMatchCount === 0 ? (
              <Trans>View all enrolments (no exact match)</Trans>
            ) : (
              <Trans>View enrolments ({targets.enrolmentMatchCount} match)</Trans>
            )}
          </Button>
        </Tooltip>
      )}
    </Flex>
  );
}

interface CrossFileRowIssue {
  rule: string;
  missingKey: string;
  severity: 'error' | 'warning';
  targetFile: string;
}

interface NATRowDetailsProps {
  natType: NatFileType;
  rowData: Record<string, string | undefined>;
  originalRowIndex: number;
  columns: Record<string, NatRuleColumn>;
  errors: readonly ValidationError[];
  crossFileIssues?: readonly CrossFileRowIssue[];
  isDuplicateRow: boolean;
  onClose?: () => void;
}

interface NATRowHeaderProps {
  originalRowIndex: number;
  natType?: NatFileType;
  onClose?: () => void;
}

function NATRowHeader({
  originalRowIndex,
  natType,
  onClose,
}: NATRowHeaderProps): React.ReactElement {
  return (
    <DrawerHeader
      backgroundColor="bg.subtle"
      w="full"
      p={4}
      borderBottomWidth={1}
      borderColor="border.default"
      position="relative"
      zIndex={2}
    >
      <Flex justify="space-between" align="center">
        <VStack align="start" spacing={0}>
          {natType ? (
            <Text fontSize="lg" fontWeight="bold">
              Edit {natType} Row Data
            </Text>
          ) : null}
          <Text fontSize="sm" fontWeight="semibold" color="fg.subtle">
            Row {originalRowIndex + 1} Details
          </Text>
        </VStack>
        <HStack spacing={2}>
          {onClose ? (
            <Button variant="ghost" size="xs" onClick={onClose} aria-label="Close drawer">
              <Icon as={X} boxSize={4} />
            </Button>
          ) : null}
        </HStack>
      </Flex>
    </DrawerHeader>
  );
}

export function NATRowDetails({
  natType,
  rowData,
  originalRowIndex,
  columns,
  errors,
  crossFileIssues = [],
  isDuplicateRow,
  onClose,
}: NATRowDetailsProps): React.ReactElement {
  const { isOpen: isErrorsExpanded, onToggle: toggleErrors } = useDisclosure({
    defaultIsOpen: false,
  });

  const { updateRowData } = useNATCache();
  const supportsStudentSync = natType === 'NAT00080';

  const rowErrors = errors.filter(
    (error) => error.rowIndex === originalRowIndex && error.severity === 'error',
  );
  const rowWarnings = errors.filter(
    (error) => error.rowIndex === originalRowIndex && error.severity === 'warning',
  );

  const { data: student } = trpcAdmin.avetmiss.sync.useQuery(
    { natFileId: natType, clientIdentifier: rowData['clientIdentifier'] ?? '' },
    {
      enabled:
        supportsStudentSync &&
        rowData['clientIdentifier'] != null &&
        rowData['clientIdentifier'] !== '' &&
        (rowErrors.length > 0 || rowWarnings.length > 0),
    },
  );

  const knownStudentId = student && !Array.isArray(student) ? student.id : undefined;

  const previousStudentRef = useRef(student);

  const refreshCacheWithStudentData = useCallback(() => {
    if (student && !Array.isArray(student) && 'studentData' in student) {
      const updatedData = transformStudentApiResponseToAvetmissCache(student);
      if (Object.keys(updatedData).length > 0) {
        updateRowData(natType, originalRowIndex, updatedData);
      }
    }
  }, [student, updateRowData, natType, originalRowIndex]);

  useEffect(() => {
    if (student && student !== previousStudentRef.current) {
      refreshCacheWithStudentData();
      previousStudentRef.current = student;
    }
  }, [student, refreshCacheWithStudentData]);

  const groupedErrors = useMemo(() => {
    return rowErrors.reduce(
      (acc, error) => {
        if (!acc[error.field]) {
          acc[error.field] = [];
        }

        // Check for duplicates based on message and code to avoid showing the same error twice
        const fieldErrors = acc[error.field] ?? [];
        const isDuplicate = fieldErrors.some(
          (existingError) =>
            existingError.message === error.message && existingError.code === error.code,
        );

        if (!isDuplicate) {
          fieldErrors.push(error);
        }

        return acc;
      },
      {} as Record<string, ValidationError[]>,
    );
  }, [rowErrors]);

  const groupedWarnings = useMemo(() => {
    return rowWarnings.reduce(
      (acc, warning) => {
        if (!acc[warning.field]) {
          acc[warning.field] = [];
        }

        // Check for duplicates based on message and code to avoid showing the same warning twice
        const fieldWarnings = acc[warning.field] ?? [];
        const isDuplicate = fieldWarnings.some(
          (existingWarning) =>
            existingWarning.message === warning.message && existingWarning.code === warning.code,
        );

        if (!isDuplicate) {
          fieldWarnings.push(warning);
        }

        return acc;
      },
      {} as Record<string, ValidationError[]>,
    );
  }, [rowWarnings]);

  const crossFileErrors = crossFileIssues.filter((i) => i.severity === 'error');
  const crossFileWarnings = crossFileIssues.filter((i) => i.severity === 'warning');

  const hasErrors = rowErrors.length > 0 || crossFileErrors.length > 0;
  const hasWarnings = rowWarnings.length > 0 || crossFileWarnings.length > 0;

  const getStatusIcon = useCallback(() => {
    if (hasErrors || isDuplicateRow) {
      return <Icon as={XCircle} boxSize={5} color="red.500" />;
    }
    if (hasWarnings) {
      return <Icon as={AlertTriangle} boxSize={5} color="yellow.500" />;
    }
    return <Icon as={AlertTriangle} boxSize={5} color="green.500" />;
  }, [hasErrors, hasWarnings, isDuplicateRow]);

  const getStatusText = useCallback((): string => {
    if (hasErrors && isDuplicateRow) {
      return t`Row has validation errors and is a duplicate`;
    }
    if (hasErrors) {
      return t`Row has validation errors`;
    }
    if (hasWarnings && isDuplicateRow) {
      return t`Row has validation warnings and is a duplicate`;
    }
    if (hasWarnings) {
      return t`Row has validation warnings`;
    }
    if (isDuplicateRow) {
      return t`Row is a duplicate record`;
    }
    return t`Row validation passed`;
  }, [hasErrors, hasWarnings, isDuplicateRow]);

  const getStatusColors = useCallback(() => {
    if (hasErrors || isDuplicateRow) {
      return { borderColor: 'red.200', bg: 'red.50' };
    }
    if (hasWarnings) {
      return { borderColor: 'yellow.200', bg: 'yellow.50' };
    }
    return { borderColor: 'green.200', bg: 'green.50' };
  }, [hasErrors, hasWarnings, isDuplicateRow]);

  const totalIssues =
    rowErrors.length +
    rowWarnings.length +
    crossFileErrors.length +
    crossFileWarnings.length +
    (isDuplicateRow ? 1 : 0);
  const totalErrorCount = rowErrors.length + crossFileErrors.length;
  const totalWarningCount = rowWarnings.length + crossFileWarnings.length;

  return (
    <Box display="flex" flexDirection="column" height="100vh">
      <Box
        position="sticky"
        top={0}
        zIndex={1}
        backgroundColor="bg.surface"
        borderBottom="1px solid"
        borderColor="gray.200"
      >
        <NATRowHeader originalRowIndex={originalRowIndex} natType={natType} onClose={onClose} />
      </Box>
      <Box flex="1" overflowY="auto" py={4}>
        <VStack spacing={4} align="stretch" px={4}>
          <RowActionBar rowData={rowData} knownStudentId={knownStudentId} />
          <Box border="1px solid" borderRadius="md" {...getStatusColors()}>
            <Box p={4} cursor="pointer" onClick={toggleErrors}>
              <Flex align="center" justify="space-between">
                <Flex align="center" gap={4}>
                  {getStatusIcon()}
                  <VStack spacing={0} align="stretch">
                    <Text fontWeight="semibold" color="gray.900">
                      {getStatusText()}
                    </Text>
                    <Text fontSize="sm" color="gray.600">
                      {totalIssues} issue{totalIssues !== 1 ? 's' : ''} found
                      {totalErrorCount > 0
                        ? ` • ${totalErrorCount} error${totalErrorCount !== 1 ? 's' : ''}`
                        : null}
                      {totalWarningCount > 0
                        ? ` • ${totalWarningCount} warning${totalWarningCount !== 1 ? 's' : ''}`
                        : null}
                      {isDuplicateRow ? ' • Duplicate record' : null}
                    </Text>
                  </VStack>
                </Flex>
                <Icon
                  as={isErrorsExpanded ? ChevronDown : ChevronRight}
                  boxSize={4}
                  color="gray.500"
                />
              </Flex>
            </Box>

            <Collapse in={isErrorsExpanded}>
              <Box borderTop="1px solid" borderColor="gray.200">
                {isDuplicateRow ? (
                  <Box p={4} borderBottom={hasErrors ? '1px solid' : 'none'} borderColor="gray.200">
                    <Flex align="center" gap={2} mb={2}>
                      <Icon as={Copy} boxSize={4} color="orange.500" />
                      <Text fontWeight="medium" color="orange.700">
                        <Trans>Duplicate Record</Trans>
                      </Text>
                    </Flex>
                    <Text fontSize="sm" color="orange.600">
                      <Trans>
                        This row has the same key combination as another record in the file.
                      </Trans>
                    </Text>
                  </Box>
                ) : null}

                {(rowErrors.length > 0 || rowWarnings.length > 0) && (
                  <Box
                    p={4}
                    borderBottom={crossFileIssues.length > 0 ? '1px solid' : 'none'}
                    borderColor="gray.200"
                  >
                    <Text
                      fontSize="xs"
                      fontWeight="semibold"
                      textTransform="uppercase"
                      color="fg.muted"
                      mb={3}
                    >
                      <Trans>In-file validation</Trans>
                    </Text>
                    <Stack spacing={3}>
                      {Object.entries(groupedErrors).map(([fieldKey, fieldErrors]) => (
                        <Box key={fieldKey}>
                          <Text fontSize="sm" fontWeight="semibold" mb={1}>
                            {columns[fieldKey]?.name ?? fieldKey}
                          </Text>
                          <Stack spacing={0.5}>
                            {fieldErrors.map((error, index) => (
                              <Flex key={index} gap={2} align="flex-start" fontSize="sm">
                                <Icon
                                  as={AlertTriangle}
                                  boxSize={iconSize.sm}
                                  color="red.500"
                                  mt={1}
                                />
                                <Text color="red.700">{error.message}</Text>
                              </Flex>
                            ))}
                          </Stack>
                        </Box>
                      ))}
                      {Object.entries(groupedWarnings).map(([fieldKey, fieldWarnings]) => (
                        <Box key={fieldKey}>
                          <Text fontSize="sm" fontWeight="semibold" mb={1}>
                            {columns[fieldKey]?.name ?? fieldKey}
                          </Text>
                          <Stack spacing={0.5}>
                            {fieldWarnings.map((warning, index) => (
                              <Flex key={index} gap={2} align="flex-start" fontSize="sm">
                                <Icon
                                  as={AlertTriangle}
                                  boxSize={iconSize.sm}
                                  color="orange.500"
                                  mt={1}
                                />
                                <Text color="orange.700">{warning.message}</Text>
                              </Flex>
                            ))}
                          </Stack>
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                )}

                {crossFileIssues.length > 0 && (
                  <Box p={4}>
                    <Text
                      fontSize="xs"
                      fontWeight="semibold"
                      textTransform="uppercase"
                      color="fg.muted"
                      mb={3}
                    >
                      <Trans>Cross-file validation</Trans>
                    </Text>
                    <Stack spacing={2}>
                      {crossFileIssues.map((issue, index) => (
                        <Flex key={index} gap={2} align="flex-start" fontSize="sm">
                          <Icon
                            as={AlertTriangle}
                            boxSize={iconSize.sm}
                            color={issue.severity === 'error' ? 'red.500' : 'orange.500'}
                            mt={1}
                          />
                          <Box>
                            <Text color={issue.severity === 'error' ? 'red.700' : 'orange.700'}>
                              {issue.rule}
                            </Text>
                            <Text fontSize="xs" color="fg.muted">
                              <Trans>Missing key</Trans> "{issue.missingKey}" <Trans>in</Trans>{' '}
                              {issue.targetFile}
                            </Text>
                          </Box>
                        </Flex>
                      ))}
                    </Stack>
                  </Box>
                )}
              </Box>
            </Collapse>
          </Box>
        </VStack>
        {supportsStudentSync && (
          <VStack spacing={6} align="stretch" mt={4}>
            {student && !Array.isArray(student) ? (
              <StudentUserDetail student={student} shouldRefetchAvetmiss={true} />
            ) : (
              <Box p={4}>
                <Trans>No student data available</Trans>
              </Box>
            )}
          </VStack>
        )}
      </Box>
      <DrawerFooter
        position="sticky"
        bottom={0}
        backgroundColor="bg.surface"
        borderTop="1px solid"
        borderColor="gray.200"
        zIndex={1}
        p={4}
      >
        <HStack spacing={3} justifyContent="space-between" w="full">
          {supportsStudentSync ? (
            <Text fontSize="sm" color="gray.600">
              <Trans>Latest student data can help resolve errors</Trans>
            </Text>
          ) : (
            <Box />
          )}
          <HStack spacing={2}>
            {supportsStudentSync && (
              <Button
                colorScheme="blue"
                variant="solid"
                leftIcon={<Icon as={RefreshCw} />}
                onClick={refreshCacheWithStudentData}
                size="sm"
              >
                <Trans>Sync Student Data</Trans>
              </Button>
            )}
            <Button variant="outline" onClick={onClose} size="sm">
              <Trans>Close</Trans>
            </Button>
          </HStack>
        </HStack>
      </DrawerFooter>
    </Box>
  );
}
